<template>
  <div class="sky">
    <div class="search-box">
      <input v-model="searchQuery" type="search" :placeholder="$t('labels.search').toUpperCase()" />
      <span>
        <icon-search />
      </span>
    </div>
    <h1>Sky Objects</h1>
    <div class="tabs">
      <router-link
        tag="button"
        :to="`/sky/${tab.id}`"
        v-for="tab in tabs"
        :key="tab.id"
        class="tab"
        :class="selectedTab === tab.id ? 'active' : ''"
      >{{ tab.label }}</router-link>
      <ToggleSwitch label="CA" v-model="toggleCA" class="toggle-switch" />
      <ToggleSwitch label="US" v-model="toggleUS" class="toggle-switch left" />
    </div>

    <data-table
      :headings="headings"
      :rows="tableData"
      :lazy-load="true"
      :search-query="searchQuery"
    >
      <template v-slot:languagesSlot="{ row, head }">
        <template v-if="row.languages.length">
          <span v-for="lang in row.languages" :key="lang">{{ lang | language }}</span>
        </template>
        <span v-else></span>
      </template>
      <template v-slot:statusSlot="{ row, head }">
        <span :class="row.status">{{ row.status }}</span>
      </template>
      <template v-slot:subscriptionPlanLogoSlot="{ row, head }">
        <div class="thumbnail" :class="row.subscriptionPlanLogo ? 'has-image' : ''">
          <img v-if="row.subscriptionPlanLogo" :src="row.subscriptionPlanLogo" alt />
        </div>
      </template>
      <template v-slot:subscriptionPlanOemVehicleImageSlot="{ row, head }">
        <div class="thumbnail" :class="row.subscriptionPlanOemVehicleImage ? 'has-image' : ''">
          <img
            v-if="row.subscriptionPlanOemVehicleImage"
            :src="row.subscriptionPlanOemVehicleImage"
            alt
          />
        </div>
      </template>
      <template v-slot:subscriptionPlanDisclosureSlot="{ row, head }">
        <span class="icon">
          <IconDisclosuresYes v-if="row.subscriptionPlanDisclosure" />
          <IconDisclosuresNo v-else />
        </span>
      </template>
      <template v-slot:editSlot="{ row, head }">
        <router-link :to="`/sky/${selectedTab}/${row.unique_id}/en-US`">Edit</router-link>
      </template>
    </data-table>
  </div>
</template>

<script>
// import skyObjects from '@/testdata/sky'
import skyFields from '@/testdata/skyFields'
import DataTable from '@/components/DataTable.vue'
import ToggleSwitch from '@/components/ToggleSwitch.vue'
import IconSearch from '@/assets/images/icons/search_icon.svg'
import IconDisclosuresYes from '@/assets/images/icons/arrow.svg'
import IconDisclosuresNo from '@/assets/images/icons/no_x.svg'
import { GET_SKY_OBJECTS } from '@/store/actions.type'

export default {
  name: 'sky',
  components: {
    DataTable,
    IconSearch,
    IconDisclosuresYes,
    IconDisclosuresNo,
    ToggleSwitch
  },
  data() {
    return {
      searchQuery: '',
      tabs: [
        {
          id: 'location',
          label: 'Location'
        },
        {
          id: 'evse',
          label: 'EVSE (Dispenser)'
        },
        {
          id: 'price-plan',
          label: 'Price Plan'
        },
        {
          id: 'subscription-plan',
          label: 'Subscription Plan'
        }
      ],
      toggleCA: true,
      toggleUS: true
      // skyObjects
    }
  },
  computed: {
    selectedTab() {
      return this.$route.params.type
    },
    typeCamel() {
      return this.selectedTab.replace(/-([a-z])/g, function(g) {
        return g[1].toUpperCase()
      })
    },
    headings() {
      if (this.selectedTab === 'subscription-plan') {
        return [
          {
            label: 'Country',
            key: 'source'
          },
          {
            label: 'Name',
            key: 'subscriptionPlanName',
            defaultSort: true
          },
          {
            label: 'Title',
            key: 'subscriptionPlanTitle'
          },
          {
            label: 'Logo',
            key: 'subscriptionPlanLogo',
            sortable: false
          },
          {
            label: 'Tag Details',
            key: 'subscriptionPlanTagDetails'
          },
          {
            label: 'OEM Image',
            key: 'subscriptionPlanOemVehicleImage',
            sortable: false
          },
          {
            label: 'Description',
            key: 'subscriptionPlanDescription'
          },
          {
            label: 'Details Header',
            key: 'subscriptionPlanDetailsHeader'
          },
          {
            label: 'Benefits',
            key: 'subscriptionPlanBenefits'
          },
          {
            label: 'Disclosures',
            key: 'subscriptionPlanDisclosure',
            sortable: false
          },
          {
            label: 'Session Description',
            key: 'subscriptionPlanSessionDescription'
          },
          {
            label: 'Languages',
            key: 'languages',
            sortable: false
          },
          {
            label: 'Thank You Text',
            key: 'subscriptionPlanThankYouText'
          },
          {
            label: 'Status',
            key: 'status'
          },
          {
            label: '',
            key: 'edit'
          }
        ]
      } else {
        return [
          {
            label: 'Country',
            key: 'source'
          },
          {
            label: 'Name',
            key: `${this.typeCamel}Name`,
            defaultSort: true
          },
          {
            label: 'Description',
            key: `${this.typeCamel}Description`
          },
          {
            label: 'Languages',
            key: 'languages',
            sortable: false
          },
          {
            label: 'Status',
            key: 'status'
          },
          {
            label: '',
            key: 'edit'
          }
        ]
      }
    },
    tableData() {
      const rows = this.$store.state.sky[this.typeCamel] || []
      rows.map(row => {
        const complete = skyFields[this.typeCamel]['en-US'].every(
          field => row[field.id]
        )
        row.status = complete ? 'complete' : 'incomplete'
        row.id = row.unique_id
      })
      return rows.filter(
        row =>
          (row.source === 'USA' && this.toggleUS) ||
          (row.source === 'CAN' && this.toggleCA)
      )
    }
  },
  methods: {
    selectTable(id) {
      this.tableNameSelected = id
    },
    getSkyObjects() {
      const loader = this.$loading.show()
      this.$store.dispatch(GET_SKY_OBJECTS, this.selectedTab).finally(() => {
        loader.hide()
      })
    }
  },
  watch: {
    selectedTab() {
      this.getSkyObjects()
    }
  },
  filters: {
    language(lang) {
      const [language, country] = lang.split('-')
      let langConverted = 'English'
      if (language === 'fr') langConverted = 'French'
      else if (language === 'sp') langConverted = 'Spanish'
      return `${langConverted} (${country})`
    }
  },
  created() {
    this.getSkyObjects()
    // const loader = this.$loading.show();
    // this.$store.dispatch(GET_USERS).finally(() => {
    //   this.users = this.$store.getters.users.map(user => {
    //     return Object.assign({}, user, { role: this.roles[user.status] });
    //   });
    //   console.log(this.users);
    //   loader.hide();
    // });
  }
}
</script>

<style lang="scss" scoped>
.sky {
  // overflow: auto;
}
.search-box {
  display: flex;
  align-items: flex-end;
  float: right;
  width: 100%;
  max-width: 400px;

  span {
    display: inline-block;
    margin-bottom: em(7);
    max-width: 20px;
    max-height: 20px;

    svg {
      fill: currentColor;
    }
  }
}

.data-table {
  ::v-deep {
    td {
      &.status {
        text-transform: uppercase;

        .incomplete {
          color: #eb3323;
        }
        .complete {
          color: #acd178;
        }
      }
      &.subscriptionPlanDisclosure {
        .icon {
          display: inline-block;
          width: 25px;
          height: 25px;
        }
      }
      &.languages span {
        white-space: nowrap;

        &:not(:last-child)::after {
          content: ', ';
        }
      }
      &.edit {
        width: 10px;
      }
    }
    .thumbnail {
      background: #adadad url('../assets/images/icons/image_icon.svg?external')
        no-repeat center center;
      background-size: 25%;
      width: 40px;
      height: 25px;

      &.has-image {
        background: none;
        height: auto;
      }
      img {
        width: 100%;
      }
    }
  }
}

.tabs {
  margin-left: -23px;
  margin-bottom: 40px;

  .tab {
    color: #0b0f4d;
    background: none;
    border-bottom: 3px solid transparent;
    border-radius: 0;
    padding: 10px 23px;

    &.active,
    &:active,
    &:focus {
      color: #35bdd1;
      border-bottom-color: #35bdd1;
    }
  }
}

.toggle-switch {
  float: right;
}
.left {
  padding-right: 35px;
}
</style>
